import { useTranslation } from "react-i18next";
import { z } from "zod";
import BookMarkSimpleIcon from "../../../assets/icons/bookmark-simple.svg?react";
import BookMarkFilledIcon from "../../../assets/icons/bookmark-filled.svg?react";
import ShareV2Icon from "../../../assets/icons/share-v2.svg?react";
import ContentHeader from "../../../components/app/headers/ContentHeader";
import ArticlePageTabs, { type ArticlePageTabsProps } from "./ArticlePageTabs";
import ClampLines from "../../../components/app/ClampLines";
import { iosGenerateHaptic } from "../../../tools/ios";
import { HapticEffect } from "../../../interfaces";
import { useCurrentUser } from "../../../domains/user/hooks/useCurrentUser";
import { useHistory } from "react-router-dom";
import { sharedContentBackDestination } from "../../../domains/room/utils/roomContentPath";
import { canGoBackInApp } from "../../../domains/app/utils/backNavigation";
import { useLocationState } from "../../../domains/app/hooks/useLocationState";
import type { Ref } from "react";

interface ArticlePageHeaderProps {
  roomId: string | null;
  /**
   * Whether the carrying room's row loaded. Back needs it: a reader who is not
   * in the room may read its article and may not open the room, so the room is
   * not a destination for them.
   */
  hasRoom: boolean;
  articleTitle: string;
  isSaved: boolean;
  onOpenPlaylists: () => void;
  onShare: () => void;
  canShare: boolean;
  headerRef: Ref<HTMLDivElement>;
  isHeroOnScreen: boolean;
  tabsProps: ArticlePageTabsProps;
  /** The reader leaves with Back (its exit, for analytics). */
  onBack: () => void;
}

export function ArticlePageHeader({
  roomId,
  hasRoom,
  articleTitle,
  isSaved,
  onOpenPlaylists,
  onShare,
  canShare,
  headerRef,
  isHeroOnScreen,
  tabsProps,
  onBack,
}: ArticlePageHeaderProps) {
  const history = useHistory();
  const { isAuthenticated } = useCurrentUser();
  const { t } = useTranslation();
  const { fromChat } = useLocationState(
    z.object({ fromChat: z.boolean().default(false) })
  );

  function handleExit() {
    iosGenerateHaptic(HapticEffect.SELECTION);
    onBack();

    const searchParams = new URLSearchParams(globalThis.location.search);
    // The notification centre opens articles in place and owns its own way out,
    // so Back must not unwind into it.
    const canGoBack =
      canGoBackInApp() && searchParams.get("notificationCenter") === null;

    if (canGoBack) {
      // Chat with Paper uses `replace` to navigate back to the article,
      // creating a duplicate article entry: [Page, Article, Article(fromChat)].
      // Skip 2 entries to land on the page before the article.
      if (fromChat && history.length > 2) {
        history.go(-2);
      } else {
        history.goBack();
      }
      return;
    }

    // Fallback when there's no history (external link, notification center,
    // etc.). A signed-out reader goes to the root, whatever the page.
    history.replace(
      sharedContentBackDestination(
        isAuthenticated
          ? { kind: "signed-in", roomId, hasRoom }
          : { kind: "signed-out" }
      )
    );
  }

  return (
    <>
      <div ref={headerRef}>
        <ContentHeader
          backLabel={t("Back")}
          onBack={handleExit}
          actions={
            <>
              {isSaved ? (
                <BookMarkFilledIcon
                  className="cursor-pointer"
                  onClick={() => {
                    onOpenPlaylists();
                  }}
                />
              ) : (
                <BookMarkSimpleIcon
                  className="cursor-pointer"
                  onClick={() => {
                    onOpenPlaylists();
                  }}
                />
              )}
              {canShare ? (
                <ShareV2Icon
                  className="cursor-pointer"
                  onClick={() => {
                    onShare();
                  }}
                />
              ) : null}
            </>
          }
        />

        <div
          className="absolute top-(--safe-area-top) inset-x-0  bg-white"
          style={{
            transform: isHeroOnScreen ? "translateY(-100%)" : "translateY(0%)",
            opacity: isHeroOnScreen ? 0 : 1,
            transition: "transform 0.5s ease-in-out, opacity 0.3s ease-in",
          }}
        >
          <div className="px-[19px] py-[12px] font-bold text-[14px] text-primary-black">
            <ClampLines lines={1}>{articleTitle}</ClampLines>
          </div>
          <ArticlePageTabs {...tabsProps} />
        </div>
      </div>
    </>
  );
}
